import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  NAKSHATRA_NAMES,
  RASHI_NAMES,
  DEFAULT_ALLY_PERSONA,
  type AiModelClass,
  type AllyMessage,
  type AllyPersona,
  type AllyTurnRequest,
  type AllyTurnResponse,
  type AstroSummary,
  type AyanamsaId,
  type BirthInput,
  type ChartFact,
  type ChartResult,
  type ConnectionDoc,
  type ConnectionIntent,
  type ConversationDoc,
  type LocaleId,
  type MemoryItem,
  type RecommendationDoc,
  type UserDoc,
} from '@astrally/shared';
import {
  buildChartFacts,
  buildTransitFacts,
  computeChart,
  computeGochar,
  computeTransits,
  currentPositions,
  deserializeChart,
  getDefaultAdapter,
  serializeChart,
  ENGINE_VERSION,
} from '@astrally/astro-engine';
import { getBackend, type Backend } from '../lib/backend';
import type { EntitlementSnapshot } from '../lib/backend/types';
import { localdb } from '../lib/localdb';
import { useI18n } from '../i18n';
import { resolveOnboardingUser } from './onboardingUser';
import {
  DEFAULT_CHAT_TITLE,
  MAX_CHAT_ENTRIES,
  chatIndexKey,
  chatKey,
  loadChatSession,
  newChatId,
  sanitiseEntries,
  sortChats,
  touchChat,
  trimTitle,
  type ChatMeta,
} from './allyChats';

export interface StoredBirthProfile {
  input: BirthInput;
  displayName: string;
}

export interface ChatEntry extends AllyMessage {
  citedFactIds?: string[];
  recommendation?: RecommendationDoc;
  /** Set on paid generations (Go Deeper / Master Reading). */
  creditCost?: number;
  modelClass?: AiModelClass;
  ts: string;
}

/** Map backend error strings to friendly, actionable copy. */
export function friendlyAiError(raw: string): { text: string; cta?: 'unlock' | 'pro' | 'credits' } {
  if (raw.includes('base-required')) {
    return { text: 'Unlock Astrally with the one-time Base purchase to continue.', cta: 'unlock' };
  }
  if (raw.includes('ai-trial-exhausted')) {
    return {
      text: 'Today’s free Astrally AI messages are used. Astrally Pro includes fair-use unlimited chat.',
      cta: 'pro',
    };
  }
  if (raw.includes('fair-use-cooldown')) {
    const m = raw.match(/ready again in (\d+) minutes/);
    return {
      text: `You’ve reached the current chat limit. Astrally will be ready again in ${m?.[1] ?? 'a few'} minutes.`,
    };
  }
  if (raw.includes('insufficient-credits')) {
    return { text: raw.match(/insufficient-credits: ([^"]+)/)?.[1] ?? 'Not enough AI credits.', cta: 'credits' };
  }
  if (raw.includes('quota-exhausted-base-lifetime')) {
    return {
      text: 'All five lifetime connection requests are used. Astrally Pro adds a generous daily allowance.',
      cta: 'pro',
    };
  }
  if (raw.includes('quota-exhausted-pro-daily')) {
    return { text: 'Today’s connection requests are used — fresh allowance tomorrow.' };
  }
  return { text: raw.replace(/^(FirebaseError|Error):\s*/g, '').slice(0, 260) };
}

interface AppStateValue {
  ready: boolean;
  backend: Backend;
  uid: string | null;
  user: UserDoc | null;
  birthProfile: StoredBirthProfile | null;
  chart: ChartResult | null;
  /**
   * The stored chart could not be restored (corrupt or unreadable cache).
   * Without this, `chart === null` is indistinguishable from "still loading"
   * and a screen waiting on it hangs forever. `recalcChart` clears it.
   */
  chartError: boolean;
  facts: ChartFact[];
  /** Messages of the ACTIVE conversation. Empty when there is no chat yet. */
  chatHistory: ChatEntry[];
  /** Every Astrally AI conversation, newest activity first. */
  chats: ChatMeta[];
  activeChatId: string | null;
  memories: MemoryItem[];
  recommendations: RecommendationDoc[];
  conversations: ConversationDoc[];
  incomingConnections: ConnectionDoc[];
  entitlement: EntitlementSnapshot | null;

  /**
   * `credentials` is required in Firebase mode (demo mode ignores it and
   * creates a local session instead).
   */
  completeOnboarding(
    profile: StoredBirthProfile,
    user: UserDoc,
    locale: LocaleId,
    credentials?: { email: string; password: string; mode: 'signup' | 'signin' },
  ): Promise<void>;
  /** Authenticate a returning account up front; returns its stored profile. */
  signInExisting(email: string, password: string): Promise<UserDoc | null>;
  recalcChart(input?: BirthInput): Promise<ChartResult | null>;
  /** Sidereal offset school. Changing it recalculates every Jyotish result. */
  ayanamsaId: AyanamsaId;
  setAyanamsa(id: AyanamsaId): Promise<void>;
  /**
   * Resolves with the ally reply, so a caller can render a one-off answer
   * without watching `chatHistory`. Pass `{ persist: false }` for generations
   * that must not join the saved conversation (e.g. a Home card): the request
   * still carries the full history for context, nothing is written back.
   *
   * Rejects if a generation is already in flight — see `allyBusy`.
   */
  sendToAlly(
    text: string,
    modelClass?: AiModelClass,
    opts?: { persist?: boolean },
  ): Promise<ChatEntry>;
  /** True while a generation is in flight; a second one is refused, not queued. */
  allyBusy: boolean;
  /**
   * Start a fresh conversation and make it active. Costs nothing — no message
   * is sent. Returns the id, and hands back the current chat unchanged if it
   * is already empty, so tapping "new" twice cannot litter the list.
   */
  newChat(persona?: AllyPersona): Promise<string>;
  /** Switch the active conversation and load its messages from the device. */
  openChat(id: string): Promise<void>;
  renameChat(id: string, title: string): Promise<void>;
  /** Removes the conversation and its messages. Deleting the last one is fine. */
  deleteChat(id: string): Promise<void>;
  refreshSocial(): Promise<void>;
  refreshMemories(): Promise<void>;
  refreshEntitlement(): Promise<EntitlementSnapshot | null>;
  purchase(productId: string, platform?: 'play' | 'mock' | 'dev'): Promise<void>;
  /** Master social opt-in. Accounts are private until this is turned on. */
  setSocialEnabled(enabled: boolean, intents?: ConnectionIntent[]): Promise<void>;
  actOnRecommendation(
    rec: RecommendationDoc,
    action: 'why' | 'request' | 'dismiss' | 'not-similar',
  ): Promise<void>;
  respondConnection(connectionId: string, accept: boolean): Promise<void>;
  resetAll(): Promise<void>;
  switchDemoUser(uid: string): Promise<void>;
}

const Ctx = createContext<AppStateValue | null>(null);

/** Derive the cloud-safe summary (signs & flags only — no degrees, no times). */
export function deriveAstroSummary(chart: ChartResult): AstroSummary {
  const positions = chart.positions ?? chart.uncertainty?.midpointPositions ?? [];
  const byPlanet = new Map(positions.map((p) => [p.planet, p] as const));
  const sun = byPlanet.get('sun');
  const venus = byPlanet.get('venus');
  const mars = byPlanet.get('mars');

  let moonSigns: number[] = [];
  let moonNaks: number[] = [];
  if (chart.uncertainty) {
    const sVal = chart.uncertainty.stable.find((f) => f.id === 'planet.moon.siderealSign')?.value;
    const vVals = chart.uncertainty.varying
      .find((f) => f.id === 'planet.moon.siderealSign')
      ?.possibilities.map((p) => p.value);
    for (const name of sVal ? [sVal] : (vVals ?? [])) {
      const idx = (RASHI_NAMES as readonly string[]).indexOf(name);
      if (idx >= 0) moonSigns.push(idx);
    }
    const sNak = chart.uncertainty.stable.find((f) => f.id === 'planet.moon.nakshatra')?.value;
    const vNaks = chart.uncertainty.varying
      .find((f) => f.id === 'planet.moon.nakshatra')
      ?.possibilities.map((p) => p.value);
    for (const name of sNak ? [sNak] : (vNaks ?? [])) {
      const idx = (NAKSHATRA_NAMES as readonly string[]).indexOf(name);
      if (idx >= 0) moonNaks.push(idx);
    }
  } else {
    const moon = byPlanet.get('moon');
    if (moon) {
      moonSigns = [moon.sidereal.sign];
      moonNaks = [moon.nakshatra.index];
    }
  }

  const mangalYoga = chart.jyotish?.yogas.find(
    (y) => y.id === 'mangal-dosha' || y.id === 'mangal-dosha-moon',
  );

  return {
    timeConfidence: chart.meta.timeConfidence,
    sunSiderealSign: sun?.sidereal.sign ?? 0,
    sunTropicalSign: sun?.tropical.sign ?? 0,
    moonSiderealSigns: moonSigns,
    moonNakshatras: moonNaks,
    lagnaSign: chart.jyotish?.lagna?.sign,
    mangalDosha: mangalYoga ? (mangalYoga.present ? 'yes' : 'no') : 'unknown',
    venusTropicalSign: venus?.tropical.sign ?? 0,
    marsTropicalSign: mars?.tropical.sign ?? 0,
    engineVersion: chart.meta.engineVersion,
  };
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const backend = useMemo(() => getBackend(), []);
  const { locale, setLocale } = useI18n();
  const [ready, setReady] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [user, setUser] = useState<UserDoc | null>(null);
  const [birthProfile, setBirthProfile] = useState<StoredBirthProfile | null>(null);
  const [chart, setChart] = useState<ChartResult | null>(null);
  const [chartError, setChartError] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [chats, setChats] = useState<ChatMeta[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  /**
   * Chat state is mirrored into refs because every writer here is async: a
   * generation started two chats ago must still land in the chat it was asked
   * in, and a `setState(prev => …)` updater cannot also persist without firing
   * a write twice under StrictMode. The refs are the source of truth for
   * writes; the state exists so React re-renders.
   */
  const chatsRef = useRef<ChatMeta[]>([]);
  const activeChatIdRef = useRef<string | null>(null);
  const chatHistoryRef = useRef<ChatEntry[]>([]);
  const [allyBusy, setAllyBusy] = useState(false);
  // The guard lives in a ref as well: two sends in the same tick would both
  // read a stale `false` off state, and every extra generation spends quota
  // or credits before we could stop it.
  const allyBusyRef = useRef(false);
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [recommendations, setRecommendations] = useState<RecommendationDoc[]>([]);
  const [conversations, setConversations] = useState<ConversationDoc[]>([]);
  const [incomingConnections, setIncomingConnections] = useState<ConnectionDoc[]>([]);
  const [entitlement, setEntitlement] = useState<EntitlementSnapshot | null>(null);
  const [ayanamsaId, setAyanamsaId] = useState<AyanamsaId>('lahiri');
  const introducedRecs = useRef(new Set<string>());

  const facts = useMemo(() => {
    if (!chart) return [];
    const base = buildChartFacts(chart);
    try {
      const moonSign = chart.positions?.find((p) => p.planet === 'moon')?.sidereal.sign;
      const ctx = currentPositions(new Date(), getDefaultAdapter());
      const gochar = computeGochar(ctx, moonSign);
      const natalPoints = (chart.positions ?? []).map((p) => ({
        id: p.planet,
        lon: p.lonTropical,
      }));
      const transits = natalPoints.length > 0 ? computeTransits(natalPoints, ctx) : [];
      return [...base, ...buildTransitFacts(gochar, transits)];
    } catch {
      return base;
    }
  }, [chart]);

  /** Write the conversation index, keeping state, ref and device in step. */
  const applyChats = useCallback(
    async (next: ChatMeta[]) => {
      const sorted = sortChats(next);
      chatsRef.current = sorted;
      setChats(sorted);
      const sessionUid = backend.currentUid();
      if (sessionUid) {
        await localdb.set('kv', chatIndexKey(sessionUid), sorted).catch(() => undefined);
      }
    },
    [backend],
  );

  const applyHistory = useCallback((entries: ChatEntry[]) => {
    chatHistoryRef.current = entries;
    setChatHistory(entries);
  }, []);

  const setActive = useCallback((id: string | null) => {
    activeChatIdRef.current = id;
    setActiveChatId(id);
  }, []);

  const loadSession = useCallback(async () => {
    const sessionUid = backend.currentUid();
    setUid(sessionUid);
    if (!sessionUid) return;
    const [u, bp, chartJson, session] = await Promise.all([
      backend.getUser(sessionUid),
      localdb.get<StoredBirthProfile>('kv', 'birthProfile'),
      localdb.get<string>('kv', `chart:${sessionUid}`),
      // Converts a pre-multi-chat account's single thread into its first chat.
      loadChatSession(localdb, sessionUid).catch(() => ({
        chats: [] as ChatMeta[],
        activeChatId: null,
        entries: [] as ChatEntry[],
        migrated: false,
      })),
    ]);
    setUser(u);
    setBirthProfile(bp ?? null);
    chatsRef.current = session.chats;
    setChats(session.chats);
    setActive(session.activeChatId);
    applyHistory(session.entries);
    setChartError(false);
    if (chartJson) {
      try {
        const { chart: restored, staleEngine } = deserializeChart(chartJson);
        if (staleEngine && bp) {
          const fresh = computeChart(bp.input);
          setChart(fresh);
          await localdb.set('kv', `chart:${sessionUid}`, serializeChart(fresh));
        } else {
          setChart(restored);
        }
      } catch {
        // The cached chart is unusable. Flag it so the chart UI can offer a
        // recalculation instead of waiting on a `chart` that never arrives.
        setChart(null);
        setChartError(true);
      }
    }
  }, [backend, applyHistory, setActive]);

  useEffect(() => {
    (async () => {
      await backend.init();
      const savedLocale = await localdb.get<LocaleId>('kv', 'locale');
      if (savedLocale) setLocale(savedLocale);
      const savedAyanamsa = await localdb.get<AyanamsaId>('kv', 'ayanamsa');
      if (savedAyanamsa) setAyanamsaId(savedAyanamsa);
      await loadSession();
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSocial = useCallback(async () => {
    if (!backend.currentUid()) return;
    const [recs, convs, conns] = await Promise.all([
      backend.listRecommendations(),
      backend.listConversations(),
      backend.listConnections(),
    ]);
    setRecommendations(recs);
    setConversations(convs);
    setIncomingConnections(conns.incoming);
  }, [backend]);

  const refreshEntitlement = useCallback(async (): Promise<EntitlementSnapshot | null> => {
    if (!backend.currentUid()) return null;
    try {
      const snap = await backend.getEntitlement();
      setEntitlement(snap);
      return snap;
    } catch {
      return null;
    }
  }, [backend]);

  const refreshMemories = useCallback(async () => {
    if (!backend.currentUid()) return;
    setMemories(await backend.listMemories());
  }, [backend]);

  useEffect(() => {
    if (ready && uid) {
      refreshSocial().catch(() => undefined);
      refreshMemories().catch(() => undefined);
      refreshEntitlement().catch(() => undefined);
    }
  }, [ready, uid, refreshSocial, refreshMemories, refreshEntitlement]);

  const purchase = useCallback(
    async (productId: string, platform?: 'play' | 'mock' | 'dev') => {
      await backend.purchase(productId, platform);
      await refreshEntitlement();
      // Recommendations only exist for accounts that opted into the social
      // layer; private accounts stay untouched.
      await backend.generateRecommendations().catch(() => undefined);
      await refreshSocial().catch(() => undefined);
    },
    [backend, refreshEntitlement, refreshSocial],
  );

  const setSocialEnabled = useCallback(
    async (enabled: boolean, intents?: ConnectionIntent[]) => {
      await backend.setSocialEnabled(enabled, intents);
      const sessionUid = backend.currentUid();
      if (sessionUid) setUser(await backend.getUser(sessionUid));
      if (enabled) await backend.generateRecommendations().catch(() => undefined);
      await refreshSocial().catch(() => undefined);
    },
    [backend, refreshSocial],
  );

  const recalcChart = useCallback(
    async (input?: BirthInput): Promise<ChartResult | null> => {
      const sessionUid = backend.currentUid();
      const bp = input
        ? ({ input, displayName: birthProfile?.displayName ?? '' } as StoredBirthProfile)
        : birthProfile;
      if (!bp || !sessionUid) return null;
      const saved = (await localdb.get<AyanamsaId>('kv', 'ayanamsa')) ?? 'lahiri';
      const fresh = computeChart(bp.input, { ayanamsaId: saved });
      setChart(fresh);
      setChartError(false); // recomputing from the birth input is the recovery path
      setBirthProfile(bp);
      await localdb.set('kv', 'birthProfile', bp);
      await localdb.set('kv', `chart:${sessionUid}`, serializeChart(fresh));
      await backend.saveAstroSummary(deriveAstroSummary(fresh));
      return fresh;
    },
    [backend, birthProfile],
  );

  /** Switching sidereal school recalculates every Jyotish result. */
  const setAyanamsa = useCallback(
    async (id: AyanamsaId) => {
      await localdb.set('kv', 'ayanamsa', id);
      setAyanamsaId(id);
      await recalcChart();
    },
    [recalcChart],
  );

  /**
   * Sign in BEFORE the birth-details questions, not after them.
   *
   * The flow used to authenticate on the very last step, so a returning user
   * typed their name, birthplace, birth time and context — and only then found
   * out the password was wrong. It also meant signing in was indistinguishable
   * from registering.
   *
   * Returns the stored profile so the remaining steps can prefill from it.
   * Birth details are deliberately NOT returned: they never leave the device,
   * so a returning user on a new device does re-enter them. That is the design,
   * and the copy now says so instead of pretending it is registration.
   */
  const signInExisting = useCallback(
    async (email: string, password: string): Promise<UserDoc | null> => {
      if (backend.kind !== 'firebase') return null;
      await backend.signIn(email, password);
      const sessionUid = backend.currentUid();
      if (!sessionUid) return null;
      return backend.getUser(sessionUid).catch(() => null);
    },
    [backend],
  );

  const completeOnboarding = useCallback(
    async (
      profile: StoredBirthProfile,
      newUser: UserDoc,
      chosenLocale: LocaleId,
      credentials?: { email: string; password: string; mode: 'signup' | 'signin' },
    ) => {
      let returning = false;
      if (backend.kind === 'firebase') {
        if (!credentials) throw new Error('An email and password are required to create your account.');
        if (credentials.mode === 'signin') {
          // Onboarding now authenticates up front via signInExisting, so by
          // the time we get here there is usually already a session. Only sign
          // in if there isn't — re-authenticating would be a wasted round trip.
          if (!backend.currentUid()) await backend.signIn(credentials.email, credentials.password);
          returning = true;
        } else {
          await backend.signUp(credentials.email, credentials.password);
        }
      } else {
        await backend.signUp('', '');
      }
      const sessionUid = backend.currentUid()!;

      /**
       * A returning account's SERVER profile wins.
       *
       * Onboarding builds a fresh UserDoc from the form, and writing that over
       * an existing profile is both wrong and impossible: it would silently
       * reset the display name and intents, and the rules reject it outright,
       * because socialEnabled / discoverable / socialConsentAtISO may only be
       * changed by the socialSetEnabled function. Signing in with connections
       * already switched on therefore failed with a bare permission-denied.
       *
       * Birth details are deliberately NOT fetched — they never leave the
       * device, so a returning user on a new device re-enters them, and that
       * is the only reason this flow asks for them again.
       */
      const existing = returning ? await backend.getUser(sessionUid).catch(() => null) : null;
      const { user: userDoc, mustSave } = resolveOnboardingUser(existing, newUser, sessionUid);
      if (mustSave) await backend.saveUser(userDoc);
      await localdb.set('kv', 'birthProfile', profile);
      await localdb.set('kv', 'locale', chosenLocale);
      setLocale(chosenLocale);
      const fresh = computeChart(profile.input);
      await localdb.set('kv', `chart:${sessionUid}`, serializeChart(fresh));
      await backend.saveAstroSummary(deriveAstroSummary(fresh));
      setUid(sessionUid);
      setUser(userDoc);
      setBirthProfile(profile);
      setChart(fresh);
      // Entitlement decides whether the ₹49 unlock gate appears next.
      await refreshEntitlement().catch(() => undefined);
      await backend.generateRecommendations().catch(() => undefined);
      await refreshSocial().catch(() => undefined);
    },
    [backend, refreshEntitlement, refreshSocial, setLocale],
  );

  /**
   * Write one conversation's messages and refresh its row in the index.
   * The 80-message cap is now PER CHAT rather than per account.
   */
  const persistChat = useCallback(
    async (chatId: string, entries: ChatEntry[]) => {
      const sessionUid = backend.currentUid();
      if (!sessionUid) return;
      // The conversation can be deleted while its reply is still in flight.
      // Writing it back would resurrect a chat the user just removed.
      if (!chatsRef.current.some((c) => c.id === chatId)) return;
      const capped = entries.slice(-MAX_CHAT_ENTRIES);
      await localdb.set('kv', chatKey(sessionUid, chatId), capped).catch(() => undefined);
      await applyChats(touchChat(chatsRef.current, chatId, capped));
    },
    [backend, applyChats],
  );

  /** The chat a message belongs to, creating the very first one on demand. */
  const ensureActiveChat = useCallback(async (): Promise<string> => {
    const current = activeChatIdRef.current;
    if (current) return current;
    const id = newChatId();
    const now = new Date().toISOString();
    setActive(id);
    applyHistory([]);
    await applyChats([
      {
        id,
        title: DEFAULT_CHAT_TITLE,
        createdAtISO: now,
        updatedAtISO: now,
        messageCount: 0,
        persona: DEFAULT_ALLY_PERSONA,
      },
      ...chatsRef.current,
    ]);
    return id;
  }, [applyChats, applyHistory, setActive]);

  /**
   * Append one entry to the ACTIVE conversation — the path recommendation
   * cards and other Ally-initiated lines take, so they can never land in a
   * chat the user is no longer looking at.
   */
  const appendToActiveChat = useCallback(
    async (entry: ChatEntry) => {
      const chatId = await ensureActiveChat();
      const next = [...chatHistoryRef.current, entry];
      applyHistory(next);
      await persistChat(chatId, next);
    },
    [applyHistory, ensureActiveChat, persistChat],
  );

  const newChat = useCallback(async (persona: AllyPersona = DEFAULT_ALLY_PERSONA): Promise<string> => {
    // An empty active chat IS a new chat. Handing it back means a double tap
    // on "new" cannot leave a trail of blank rows in the list.
    const active = chatsRef.current.find((c) => c.id === activeChatIdRef.current);
    if (active && active.messageCount === 0 && chatHistoryRef.current.length === 0) {
      // Reuse it, but honour the persona asked for — otherwise picking the
      // Western astrologer on an untouched chat would silently do nothing.
      if (active.persona !== persona) {
        await applyChats(
          chatsRef.current.map((c) => (c.id === active.id ? { ...c, persona } : c)),
        );
      }
      return active.id;
    }
    const sessionUid = backend.currentUid();
    /**
     * Blank chats abandoned earlier are swept, so the list does not fill with
     * rows called "New chat". Two guards keep that from ever costing anything:
     * the row must ALSO hold nothing on disk (a wrong `messageCount` in a
     * damaged index must not delete real messages), and the chat being left
     * behind right now is exempt — its first message may still be in flight.
     */
    const previous = activeChatIdRef.current;
    const blank: string[] = [];
    if (sessionUid) {
      for (const c of chatsRef.current) {
        if (c.messageCount > 0 || c.id === previous) continue;
        const stored = sanitiseEntries(
          await localdb.get<unknown>('kv', chatKey(sessionUid, c.id)).catch(() => undefined),
        );
        if (stored.length === 0) blank.push(c.id);
      }
    }
    const id = newChatId();
    const now = new Date().toISOString();
    setActive(id);
    applyHistory([]);
    await applyChats([
      { id, title: DEFAULT_CHAT_TITLE, createdAtISO: now, updatedAtISO: now, messageCount: 0, persona },
      ...chatsRef.current.filter((c) => !blank.includes(c.id)),
    ]);
    if (sessionUid) {
      for (const c of blank) await localdb.del('kv', chatKey(sessionUid, c)).catch(() => undefined);
    }
    return id;
  }, [applyChats, applyHistory, backend, setActive]);

  const openChat = useCallback(
    async (id: string) => {
      if (!chatsRef.current.some((c) => c.id === id)) return;
      setActive(id);
      const sessionUid = backend.currentUid();
      const raw = sessionUid
        ? await localdb.get<unknown>('kv', chatKey(sessionUid, id)).catch(() => undefined)
        : undefined;
      // Two quick taps race their reads; only the chat still on screen wins.
      if (activeChatIdRef.current !== id) return;
      applyHistory(sanitiseEntries(raw));
    },
    [applyHistory, backend, setActive],
  );

  const renameChat = useCallback(
    async (id: string, title: string) => {
      const clean = trimTitle(title);
      await applyChats(chatsRef.current.map((c) => (c.id === id ? { ...c, title: clean } : c)));
    },
    [applyChats],
  );

  const deleteChat = useCallback(
    async (id: string) => {
      const remaining = chatsRef.current.filter((c) => c.id !== id);
      await applyChats(remaining);
      const sessionUid = backend.currentUid();
      if (sessionUid) await localdb.del('kv', chatKey(sessionUid, id)).catch(() => undefined);
      if (activeChatIdRef.current !== id) return;
      // Deleting the chat you are reading falls back to the next most recent
      // one — and to a clean empty screen when that was the last one.
      const fallback = remaining[0]?.id ?? null;
      setActive(fallback);
      if (!fallback || !sessionUid) {
        applyHistory([]);
        return;
      }
      const raw = await localdb.get<unknown>('kv', chatKey(sessionUid, fallback)).catch(() => undefined);
      if (activeChatIdRef.current !== fallback) return;
      applyHistory(sanitiseEntries(raw));
    },
    [applyChats, applyHistory, backend, setActive],
  );

  /**
   * NOTE: this function's identity changes whenever the chart, facts, locale
   * or memories change. Never put `sendToAlly` in an effect's dependency array
   * — the effect would re-fire and generate again, spending quota each time.
   */
  const sendToAlly = useCallback(
    async (
      text: string,
      modelClass: AiModelClass = 'standard',
      opts?: { persist?: boolean },
    ): Promise<ChatEntry> => {
      // Refuse rather than queue: a second in-flight turn spends quota or
      // credits, and its reply would land out of order.
      if (allyBusyRef.current) throw new Error('ally-busy');
      allyBusyRef.current = true;
      setAllyBusy(true);
      try {
        const persist = opts?.persist !== false;
        // The turn is bound to ONE conversation up front. If the user opens a
        // different chat while it is in flight, the reply still belongs here.
        const chatId = persist ? await ensureActiveChat() : null;
        const userEntry: ChatEntry = {
          role: 'user',
          text,
          modelClass,
          ts: new Date().toISOString(),
        };
        // A transient turn still sends the active chat's context — it just
        // never writes anything back.
        const base = [...chatHistoryRef.current, userEntry];
        if (persist) applyHistory(base);

        const confirmed = memories
          .filter((m) => m.status === 'confirmed')
          .slice(0, 30)
          .map((m) => ({ category: m.category, content: m.content }));

        // If the user is asking about the latest recommendation, attach context.
        const lastRec = [...base]
          .reverse()
          .find((e) => e.recommendation)?.recommendation;

        const req: AllyTurnRequest = {
          locale,
          timeConfidence: chart?.meta.timeConfidence ?? 'unknown',
          modelClass,
          generationId:
            modelClass !== 'standard'
              ? `g-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
              : undefined,
          // The astrologer this conversation is with. Absent on chats created
          // before personas existed; the server defaults those to Jyotish,
          // which is what they always were.
          persona:
            chatsRef.current.find((c) => c.id === activeChatIdRef.current)?.persona ??
            DEFAULT_ALLY_PERSONA,
          messages: base.slice(-12).map(({ role, text: t }) => ({ role, text: t })),
          facts: facts.slice(0, 120),
          memories: confirmed,
          recommendationContext: lastRec
            ? {
                // NOT the uid. It used to send `candidateUid` with the demo
                // prefix stripped — a 28-character random identifier that the
                // model cannot use for anything, costs about ten tokens on
                // every turn that carries a recommendation, and hands another
                // user's account id to a third-party AI provider. "This
                // person" reads better in an answer anyway.
                candidateDisplayName: 'this person',
                grades: Object.fromEntries(
                  Object.entries(lastRec.grades).map(([k, v]) => [k, String(v)]),
                ),
                sharedInterests: lastRec.sharedInterests,
                rationale: lastRec.rationale,
              }
            : undefined,
        };

        let response: AllyTurnResponse;
        try {
          response = await backend.allyTurn(req);
        } catch (e) {
          const friendly = friendlyAiError(String(e));
          response = {
            reply: friendly.text,
            citedFactIds: [],
            memorySuggestions: [],
            provider: 'mock',
          };
        }
        if (response.creditCost) {
          await refreshEntitlement().catch(() => undefined);
        }

        const allyEntry: ChatEntry = {
          role: 'ally',
          text: response.reply,
          citedFactIds: response.citedFactIds,
          creditCost: response.creditCost,
          modelClass,
          ts: new Date().toISOString(),
        };
        if (persist && chatId) {
          const next = [...base, allyEntry];
          // Only repaint the screen if the chat that asked is still the one on
          // screen; the conversation itself is written either way.
          if (activeChatIdRef.current === chatId) applyHistory(next);
          await persistChat(chatId, next);
        }
        if (response.memorySuggestions.length > 0) {
          await refreshMemories();
        }
        return allyEntry;
      } finally {
        allyBusyRef.current = false;
        setAllyBusy(false);
      }
    },
    [
      applyHistory,
      backend,
      chart,
      ensureActiveChat,
      facts,
      locale,
      memories,
      persistChat,
      refreshMemories,
      refreshEntitlement,
    ],
  );

  /**
   * Surface fresh pending recommendations as conversational Ally entries —
   * AT MOST TWO PER HOUR.
   *
   * Without the cap, a batch of recommendations turned the chat into a feed:
   * one "I found someone…" after another, which reads as spam from the one
   * voice in the app that must never feel like spam. A throttled rec is not
   * lost — it stays `pending` and simply waits for a later hour.
   */
  useEffect(() => {
    if (!ready || recommendations.length === 0) return;
    const pending = recommendations.filter(
      (r) => r.status === 'pending' && !introducedRecs.current.has(r.id),
    );
    if (pending.length === 0) return;
    let cancelled = false;
    void (async () => {
      const sessionUid = backend.currentUid();
      const logKey = `recIntroLog:${sessionUid ?? 'anon'}`;
      const hourAgo = Date.now() - 60 * 60 * 1000;
      const log = ((await localdb.get<number[]>('kv', logKey).catch(() => undefined)) ?? []).filter(
        (ts) => ts > hourAgo,
      );
      if (cancelled || log.length >= 2) return;
      const rec = pending[0];
      introducedRecs.current.add(rec.id);
      await localdb.set('kv', logKey, [...log, Date.now()]).catch(() => undefined);
      const entry: ChatEntry = {
        role: 'ally',
        text:
          `I found someone I think you may benefit from speaking with. ` +
          rec.rationale.slice(0, 2).join(' ') +
          ` Overall compatibility grade: ${rec.grades.overall}.`,
        recommendation: rec,
        ts: new Date().toISOString(),
      };
      // Into the chat the user is actually reading — and into a first chat of
      // its own if they have never sent a message.
      await appendToActiveChat(entry);
      void backend.setRecommendationStatus(rec.id, 'seen').catch(() => undefined);
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, recommendations, backend, appendToActiveChat]);

  const actOnRecommendation = useCallback(
    async (rec: RecommendationDoc, action: 'why' | 'request' | 'dismiss' | 'not-similar') => {
      switch (action) {
        case 'why':
          // The card stays tappable during a generation; swallow the second tap
          // rather than reject into an onClick handler that has no catch.
          if (allyBusyRef.current) return;
          await sendToAlly('Why this person?');
          return;
        case 'request': {
          let text: string;
          try {
            const res = await backend.connectionRequest(rec.candidateUid);
            await backend.setRecommendationStatus(rec.id, 'seen');
            text =
              `Connection request sent — they can accept or decline privately. ` +
              (res.quota.kind === 'base-lifetime'
                ? `${res.quota.remaining} of your lifetime requests remain.`
                : `${res.quota.remaining} requests left today.`);
            await refreshEntitlement();
          } catch (e) {
            text = friendlyAiError(String(e)).text;
          }
          await appendToActiveChat({ role: 'ally', text, ts: new Date().toISOString() });
          break;
        }
        case 'dismiss':
          await backend.setRecommendationStatus(rec.id, 'dismissed');
          break;
        case 'not-similar':
          await backend.setRecommendationStatus(rec.id, 'not-similar');
          break;
      }
      await refreshSocial();
    },
    [appendToActiveChat, backend, refreshEntitlement, refreshSocial, sendToAlly],
  );

  const respondConnection = useCallback(
    async (connectionId: string, accept: boolean) => {
      await backend.connectionRespond(connectionId, accept);
      await refreshSocial();
    },
    [backend, refreshSocial],
  );

  /**
   * "Delete my account" — and with it everything this device is holding.
   *
   * Clearing React state is cosmetic: `birthProfile` (raw birth date, exact
   * time, lat/lon), `chart:{uid}` and every `allyChat:{uid}:*` transcript are
   * in IndexedDB, which survives a reload, a reinstall of the web app and —
   * because android:allowBackup="true" — is eligible for Android backup. This
   * used to delete the cloud account and leave all of it in place.
   *
   * Both backends now wipe the device inside deleteAccount() (demo mode always
   * did). The wipe is repeated here so the guarantee belongs to the app rather
   * than to whichever backend happens to be wired up, and so a backend added
   * later cannot reintroduce the leak by omission. Clearing empty stores is a
   * no-op, and it runs only after deleteAccount() resolves — a failed deletion
   * must leave the device untouched, since the account is still live.
   */
  const resetAll = useCallback(async () => {
    await backend.deleteAccount();
    await localdb.wipeEverything();
    setUid(null);
    setUser(null);
    setBirthProfile(null);
    setChart(null);
    setChartError(false);
    applyHistory([]);
    chatsRef.current = [];
    setChats([]);
    setActive(null);
    setMemories([]);
    setRecommendations([]);
    setConversations([]);
    setIncomingConnections([]);
    setEntitlement(null);
  }, [applyHistory, backend, setActive]);

  const switchDemoUser = useCallback(
    async (targetUid: string) => {
      if (!backend.switchDemoUser) return;
      await backend.switchDemoUser(targetUid);
      introducedRecs.current.clear();
      applyHistory([]);
      chatsRef.current = [];
      setChats([]);
      setActive(null);
      setChart(null);
      setChartError(false);
      await loadSession();
      await refreshSocial();
      await refreshMemories();
      await refreshEntitlement();
    },
    [
      applyHistory,
      backend,
      loadSession,
      refreshMemories,
      refreshSocial,
      refreshEntitlement,
      setActive,
    ],
  );

  const value: AppStateValue = {
    ready,
    backend,
    uid,
    user,
    birthProfile,
    chart,
    chartError,
    facts,
    chatHistory,
    chats,
    activeChatId,
    memories,
    recommendations,
    conversations,
    incomingConnections,
    entitlement,
    completeOnboarding,
    signInExisting,
    recalcChart,
    ayanamsaId,
    setAyanamsa,
    sendToAlly,
    allyBusy,
    newChat,
    openChat,
    renameChat,
    deleteChat,
    refreshSocial,
    refreshMemories,
    refreshEntitlement,
    purchase,
    setSocialEnabled,
    actOnRecommendation,
    respondConnection,
    resetAll,
    switchDemoUser,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppStateValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppStateProvider');
  return v;
}

export { ENGINE_VERSION };
export type { ChatMeta } from './allyChats';
